<script lang="ts" setup>
  import { APPLE_MAPS_URL, GOOGLE_MAPS_URL } from '@/constants/maps'
  import { TABLE_BOOKING_URL } from '@/constants/restaurant'
  import { getMapURLQueryParams } from '@/utils/maps'
  import { checkIfAppleDevice } from '@/utils/platform'
  import { filterOrderTypeTags, getRestaurantTags, prependBrandName } from '@/utils/restaurant'
  import type { Restaurant, TagIcon } from '@/types/api/restaurant'
  import { gaEventGroupBookingClicked } from '@/composables/analytics'

  const props = defineProps<{
    restaurant: Restaurant
  }>()

  const restaurantName = computed(() => prependBrandName(props.restaurant.name) || '')

  const breadcrumbSteps = computed(() => {
    return [
      {
        label: "Grill'd",
        link: '/'
      },
      {
        label: 'Find Restaurant',
        link: '/restaurants'
      },
      {
        label: restaurantName.value
      }
    ]
  })

  /**
   * @description Combine order types and restaurant marketing tags to create a list of chips
   */
  const chips = computed<TagIcon[]>(() => {
    const marketingTags =
      props.restaurant.tags && Object.hasOwn(props.restaurant.tags, 'marketing')
        ? props.restaurant.tags.marketing
        : undefined
    const filteredOrderTypes = filterOrderTypeTags(props.restaurant.orderTypes)

    return getRestaurantTags(filteredOrderTypes, marketingTags)
  })

  const { address } = useAddressMapsLink()

  /**
   * @description Creates a link for the address to link to Apple Maps or Google Maps
   */
  function useAddressMapsLink() {
    const address = ref<{ href: string; target: string | undefined }>({
      href: `${GOOGLE_MAPS_URL}${getMapURLQueryParams({
        name: props.restaurant.name,
        address: props.restaurant.address,
        latitude: props.restaurant.latitude,
        longitude: props.restaurant.longitude
      })}`,
      target: '_blank'
    })

    /**
     * @description Update the maps link for Apple devices to use Apple Maps instead of Google Maps.
     */
    function updateMapLinkForAppleDevices() {
      const isAppleDevice = checkIfAppleDevice()

      if (isAppleDevice) {
        const queryString = getMapURLQueryParams({
          name: "Grill'd", // NOTE: All Grill'd restaurants are named "Grill'd" on Apple Maps
          address: props.restaurant.address,
          latitude: props.restaurant.latitude,
          longitude: props.restaurant.longitude
        })

        address.value = {
          href: `${APPLE_MAPS_URL}${queryString}`,
          target: undefined
        }
      }
    }

    onMounted(() => {
      /**
       * @note This function is called on mount instead of computed as it requires the user agent, not available during SSR.
       */
      updateMapLinkForAppleDevices()
    })

    return {
      address: readonly(address)
    }
  }
</script>

<template>
  <section class="module-paddings-h section-restaurant-header">
    <div class="restaurant-overview">
      <CBreadcrumbs :steps="breadcrumbSteps" class="page-breadcrumbs" />

      <h1 class="restaurant-name">{{ restaurantName }}</h1>

      <div v-if="chips.length" class="restaurant-chips">
        <CChip v-for="(chip, index) in chips" :key="index" :icon="chip.icon" :text="chip.name" />
      </div>
    </div>

    <CDivider />

    <div class="restaurant-info">
      <div v-if="restaurant.hours.length" class="restaurant-details">
        <div class="restaurant-details-item">
          <icon-ionic-time-outline class="details-icon" color="var(--color-base-red)" height="24" width="24" />
          <COpeningHoursDropdown :hours="restaurant.hours" />
        </div>

        <div v-if="restaurant.address" class="restaurant-details-item">
          <icon-ionic-location-outline class="details-icon" color="var(--color-base-red)" height="24" width="24" />
          <a :href="address.href" :target="address.target" class="details-text details-text-link">
            {{ restaurant.address }}
          </a>
        </div>

        <div v-if="restaurant.obeeId" class="restaurant-details-item">
          <icon-ionic-calendar-outline class="details-icon" color="var(--color-base-red)" height="24" width="24" />
          <a
            :href="`${TABLE_BOOKING_URL}/${restaurant.obeeId}`"
            class="details-text details-text-link"
            target="_blank"
            @click="gaEventGroupBookingClicked('details')"
          >
            Book Your Table
          </a>
        </div>

        <div v-if="restaurant.phoneNumber" class="restaurant-details-item">
          <icon-ionic-call-outline class="details-icon" color="var(--color-base-red)" height="24" width="24" />
          <a :href="`tel:${restaurant.phoneNumber}`" class="details-text">{{ restaurant.phoneNumber }}</a>
        </div>
      </div>

      <div class="restaurant-description">
        <p>{{ restaurant.description }}</p>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
  .section-restaurant-header {
    padding: 0 var(--padding-x);
    background-color: var(--color-secondary-paper-light);

    .restaurant-overview {
      padding: 48px 0 32px;

      .page-breadcrumbs {
        margin-bottom: 8px;
      }

      .restaurant-name {
        @include typography-header-3;
        margin-bottom: 16px;
      }

      .restaurant-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      @include screenMdAndUp {
        padding-bottom: 24px;
      }
    }

    .restaurant-info {
      display: flex;
      flex-direction: column;
      row-gap: 32px;

      padding: 32px 0;

      .restaurant-details {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        row-gap: 16px;

        .restaurant-details-item {
          display: flex;
          align-items: center;
          justify-content: center;
          column-gap: 16px;

          padding: 8px 0;

          .details-text {
            @include typography-body;
            color: var(--color-text-body);
            cursor: pointer;
          }

          .details-text-link {
            text-decoration-line: underline;
          }
        }
      }

      @include screenMdAndUp {
        flex-direction: row;
        padding-bottom: 48px;
        column-gap: 32px;

        > * {
          flex: 1;
        }
      }
    }
  }
</style>
